/* global BigInt */ // ES2020; the app's ESLint env predates it
/**
 * ksaMoney - pure display helpers for the GLOBAL KSA costs, profit and
 * exchange-rate screens (KsaCostsPanel, KsaRatesPanel, the KSA page strip).
 * Tested in __tests__/ksaMoney.test.js.
 *
 * Every amount shown carries its currency code ("SAR 1,234.56"), money is
 * always 2 decimals, and a rate is "units of the project currency per 1 unit
 * of the purchase currency" - the same meaning the server stores.
 */
import { localDay } from '../../utils/localDay';
import { amount2 } from '../../utils/format';

// The currencies a KSA purchase or project can be in (the server's list).
export const CURRENCIES = ['SAR', 'USD', 'EUR', 'GBP', 'ZAR', 'INR', 'AED'];
// Fixed to the US dollar, never fetched: 1 USD = 3.75 SAR, 1 AED = 1/3.6725 USD.
export const PEGGED = ['SAR', 'USD', 'AED'];
export const CURRENCY_NAMES = {
  SAR: 'Saudi riyal',
  USD: 'US dollar',
  EUR: 'Euro',
  GBP: 'British pound',
  ZAR: 'South African rand',
  INR: 'Indian rupee',
  AED: 'UAE dirham',
};
export const PURCHASE_STATUSES = ['Planned', 'Ordered', 'Paid'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The fx code of a stored currency label: 'US Dollar' is USD, anything else is itself. '' when blank. */
export function fxCode(label) {
  const s = String(label == null ? '' : label).trim();
  return s === 'US Dollar' ? 'USD' : s;
}

/** A number, or null for blank / not a number. The API sends NUMERIC columns as strings. */
export function num(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** Round to cents, half away from zero (what Postgres NUMERIC does), without float drift (1.005 is 1.01). */
export function round2(v) {
  const n = num(v);
  if (n == null) return null;
  const sign = n < 0 ? -1 : 1;
  const abs = Math.abs(n);
  const s = String(abs);
  const r = s.includes('e') ? Math.round(abs * 100) / 100 : Number(`${Math.round(Number(`${s}e2`))}e-2`);
  return r === 0 ? 0 : sign * r;
}

const grouped = amount2;

/** "SAR 1,234.56"; negatives "SAR -1,234.56"; '-' when there is no number. Zero shows as "SAR 0.00". */
export function fmtMoney(v, code) {
  const n = round2(v);
  if (n == null) return '-';
  const c = code ? `${code} ` : '';
  return `${c}${n < 0 ? '-' : ''}${grouped(Math.abs(n))}`;
}

/** "12.34%", or '-' when there is none (no revenue). */
export function fmtPct(v) {
  const n = round2(v);
  return n == null ? '-' : `${n.toFixed(2)}%`;
}

const trimZeros = (s) => (s.includes('.') && !s.includes('e') ? s.replace(/0+$/, '').replace(/\.$/, '') : s);

/**
 * A rate for a sentence or a table cell: 4 decimals from 1 up, 4 significant
 * digits below 1 ("1 ZAR = 0.2288 SAR", "1 SAR = 0.2667 USD", "1 USD = 3.75 SAR").
 * The exact rate is fmtRateExact.
 */
export function fmtRate(v) {
  const n = num(v);
  if (n == null || n <= 0) return '-';
  const s = n >= 1 ? n.toFixed(4) : n.toPrecision(4);
  return s.includes('e') ? trimZeros(n.toFixed(10)) : trimZeros(s);
}

/** A rate to 6 significant digits (the rates table, the purchase form's rate box). */
export function fmtRateExact(v) {
  const n = num(v);
  if (n == null || n <= 0) return '';
  const s = n.toPrecision(6);
  return s.includes('e') ? trimZeros(n.toFixed(10)) : trimZeros(s);
}

/** A rate exactly as the database keeps it (NUMERIC(20,10)): up to 10 decimals, trailing zeros dropped. */
export function fmtRateFull(v) {
  const n = num(v);
  return n == null || n <= 0 ? '' : trimZeros(n.toFixed(10));
}

/** "1 ZAR = 0.2288 SAR" */
export function rateText(from, rate, to) {
  return `1 ${from} = ${fmtRate(rate)} ${to}`;
}

/** 'YYYY-MM' of a 'YYYY-MM-DD' (or an ISO timestamp, or 'YYYY-MM'); '' when it is not one. */
export function monthOf(d) {
  const m = String(d || '').match(/^(\d{4})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}` : '';
}

/** "Sep 2026" for '2026-09' / '2026-09-01' / a DATE timestamp; '' when it is not one. */
export function monthLabel(d) {
  const m = monthOf(d);
  if (!m) return '';
  const i = Number(m.slice(5, 7)) - 1;
  return i >= 0 && i < 12 ? `${MONTHS[i]} ${m.slice(0, 4)}` : '';
}

const pad = (n) => String(n).padStart(2, '0');

/** Today in the viewer's own time zone, 'YYYY-MM-DD' (toISOString would give tomorrow in the evening in the US). */
export function todayLocal(now = new Date()) {
  return localDay(now);
}

/** This month and the `n - 1` before it, newest first: ['2026-09', '2026-08', ...]. */
export function lastMonths(n = 12, now = new Date()) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
  }
  return out;
}

/** A real calendar date written 'YYYY-MM-DD' (not 2026-02-30). */
export function isRealDate(s) {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]);
}

/** A typed amount: 0 or more, at most 2 decimals, commas allowed. The number, or null when it is not one. */
export function parseAmount(s) {
  const raw = String(s == null ? '' : s).trim();
  // a comma is only a thousands separator (50,000.00); "50,5" is a decimal comma, refused
  if (raw.includes(',') && !/^\d{1,3}(,\d{3})+(\.\d*)?$/.test(raw)) return null;
  const t = raw.replace(/,/g, '');
  return /^\d+(\.\d{1,2})?$/.test(t) ? Number(t) : null;
}

/** A typed rate: a number above 0. The number, or null. */
export function parseRate(s) {
  const t = String(s == null ? '' : s).trim();
  // no rate is in the thousands, so any comma is a decimal comma ("0,2288"): refused, never guessed
  if (!/^\d*\.?\d+$/.test(t)) return null;
  const n = Number(t);
  return n > 0 ? n : null;
}

// A decimal as a BigInt scaled to `dp` places, rounded half away from zero. null when not a number.
function scaled(v, dp) {
  const n = num(v);
  if (n == null) return null;
  let s = String(Math.abs(n));
  if (s.includes('e')) s = Math.abs(n).toFixed(20);
  const [int, frac = ''] = s.split('.');
  const digits = BigInt(`${int}${frac.padEnd(dp + 1, '0').slice(0, dp + 1)}`);
  const ten = BigInt(10);
  let out = digits / ten + (digits % ten >= BigInt(5) ? BigInt(1) : BigInt(0));
  if (n < 0) out = -out;
  return out;
}

/**
 * amount x rate in the project currency, to the cent, in exact decimal
 * arithmetic (the database's NUMERIC multiply, not float): the preview on the
 * purchase form shows the same cents the server will store.
 */
export function convertAmount(amount, rate) {
  const a = scaled(amount, 2); // cents
  const r = scaled(rate, 10); // the rate column is NUMERIC(20,10)
  if (a == null || r == null) return null;
  const prod = a * r; // scaled 1e12
  const unit = BigInt(10) ** BigInt(10);
  const neg = prod < BigInt(0);
  const abs = neg ? -prod : prod;
  let cents = abs / unit;
  if ((abs % unit) * BigInt(2) >= unit) cents += BigInt(1);
  const v = Number(cents) / 100;
  return neg && v !== 0 ? -v : v;
}

/** The fx-rates row of one currency, from a GET /fx-rates response's `rows`. */
export function rateRow(rows, code) {
  return (rows || []).find((r) => r && r.currency === code) || null;
}

/**
 * The month's rate for 1 `from` in `to` units (e.g. SAR per ZAR), from the
 * fx-rates rows; 1 for the same currency; null when either has no rate yet.
 */
export function monthlyRate(rows, from, to) {
  if (!from || !to) return null;
  if (from === to) return 1;
  const f = rateRow(rows, from);
  const t = rateRow(rows, to);
  const fs = f && num(f.sar_per_unit);
  const ts = t && num(t.sar_per_unit);
  // the server's own rule (lib/fx.js rateBetween): SAR per unit over SAR per unit, to 10 significant digits
  const sig10 = (x) => Number(x.toPrecision(10));
  if (fs && ts) return to === 'SAR' ? fs : sig10(fs / ts);
  const fu = f && num(f.usd_per_unit);
  const tu = t && num(t.usd_per_unit);
  return fu && tu ? sig10(fu / tu) : null;
}

/** Where a purchase's rate came from, for its table cell: "Sep 2026 rate", "bank rate", "same currency". */
export function purchaseRateSource(p) {
  const src = p && p.rate_source;
  if (src === 'actual') return 'bank rate';
  if (src === 'same') return 'same currency';
  const m = monthLabel(p && p.purchase_date);
  return m ? `${m} rate` : 'monthly rate';
}

/** Where a month's rate came from, for the rates table. `fmtDay` turns a DATE into text. */
export function rateSourceText(row, fmtDay = (d) => String(d || '').slice(0, 10)) {
  const src = row && row.source;
  if (src === 'peg') return 'Fixed peg';
  if (src === 'ECB') return row.rate_date ? `ECB ${fmtDay(row.rate_date)}` : 'ECB';
  if (src === 'manual') return `Manual by ${row.updated_by || 'unknown'}`;
  return 'No rate yet';
}

/**
 * The exchange-rate effect line. fx_effect > 0 means the goods cost MORE than
 * at quote time (a loss), so it shows with a minus: "SAR -1,234.56 (cost more)".
 */
export function fxEffectText(fxEffect, code) {
  const n = round2(fxEffect);
  if (n == null) return { text: '-', tone: 'none' };
  if (n === 0) return { text: `${code} 0.00 (no change)`, tone: 'none' };
  const amount = grouped(Math.abs(n));
  return n > 0 ? { text: `${code} -${amount} (cost more)`, tone: 'loss' } : { text: `${code} +${amount} (cost less)`, tone: 'gain' };
}

/** Add two money values to the cent (labor + purchases on the page strip). */
export function addMoney(...vals) {
  const parts = vals.map(num);
  if (parts.every((v) => v == null)) return null;
  return round2(parts.reduce((s, v) => s + Math.round((v || 0) * 100), 0) / 100);
}
